/**
 * ExperimentalChart
 *
 * Line chart showing weekly billed hours since Hourglass go-live,
 * with a dotted reference line at the pre-Hourglass weekly average.
 *
 * The line color transitions from deleted-state red (0% utilization) to spruce
 * (100% utilization) based on each week's Hourglass utilization percentage.
 * This is achieved via an SVG linearGradient with stops at each data point's
 * x-position, colored by mixing semantic palette tokens.
 */

import { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ResponsiveContainer,
} from "recharts";
import type { ExperimentalProfData } from "../../types/dashboard";

const COLORS = {
  preHgBand: "var(--muted-foreground)",
} as const;

/** Mix between semantic low/high colors based on utilization. */
export function utilizationColor(pct: number): string {
  const t = Math.max(0, Math.min(pct, 100));
  return `color-mix(in srgb, var(--status-deleted) ${100 - t}%, var(--chart-1) ${t}%)`;
}

interface ChartPoint {
  week_label: string;
  hours: number;
  utilization_pct: number;
  hg_hours: number;
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { payload: ChartPoint; value: number }[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;

  const pt = payload[0];
  const color = utilizationColor(pt.payload.utilization_pct);

  return (
    <div
      style={{
        backgroundColor: "var(--popover)",
        border: "1px solid var(--border)",
        borderRadius: "8px",
        padding: "10px 14px",
        fontSize: "13px",
      }}
    >
      <p className="font-semibold text-foreground mb-2">{label}</p>
      <p style={{ color }}>
        Weekly Hours: {pt.value.toFixed(1)}h
      </p>
      <p style={{ color }}>
        HG Hours: {pt.payload.hg_hours.toFixed(1)}h
      </p>
      <p style={{ color }}>
        Utilization: {pt.payload.utilization_pct.toFixed(1)}%
      </p>
    </div>
  );
}

/** Custom dot renderer — each dot colored by its utilization. */
function UtilizationDot(props: {
  cx?: number;
  cy?: number;
  payload?: ChartPoint;
  r?: number;
}) {
  const { cx, cy, payload, r = 4 } = props;
  if (cx == null || cy == null || !payload) return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill={utilizationColor(payload.utilization_pct)}
      stroke="none"
    />
  );
}

export default function ExperimentalChart({ data }: { data: ExperimentalProfData }) {
  const chartData: ChartPoint[] = useMemo(
    () =>
      data.weekly_hours.map((w) => ({
        week_label: w.week_label,
        hours: w.hours,
        utilization_pct: w.utilization_pct,
        hg_hours: w.hg_hours,
      })),
    [data.weekly_hours]
  );

  // Build SVG gradient stops — one per data point at its proportional x offset
  // Use prof_id in the gradient ID to avoid SVG ID collisions across multiple charts
  const gradientId = `utilGradient-${data.prof_id}`;
  const gradientStops = useMemo(() => {
    if (chartData.length < 2) return [];
    const last = chartData.length - 1;
    return chartData.map((pt, i) => ({
      offset: `${(i / last) * 100}%`,
      color: utilizationColor(pt.utilization_pct),
    }));
  }, [chartData]);

  if (chartData.length === 0) {
    return (
      <p className="text-center text-muted-foreground text-sm py-4">
        No weekly data available.
      </p>
    );
  }

  // Show ~8 X-axis labels regardless of data length
  const xInterval = Math.max(0, Math.floor(chartData.length / 8) - 1);

  // Y-axis must always include the top of the pre-HG avg±std band
  const preHgStd = data.pre_hg_std ?? 0;
  const preHgBandTop = data.pre_hg_avg + preHgStd;
  const maxHours = Math.max(...chartData.map((p) => p.hours), preHgBandTop);
  const yMax = Math.ceil(maxHours * 1.1);

  return (
    <div>
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={chartData} margin={{ top: 4, right: 40, bottom: 4, left: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
              {gradientStops.map((stop, i) => (
                <stop key={i} offset={stop.offset} stopColor={stop.color} />
              ))}
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis
            dataKey="week_label"
            tick={{ fontSize: 11 }}
            stroke="var(--muted-foreground)"
            interval={xInterval}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            stroke="var(--muted-foreground)"
            tickFormatter={(v) => `${v}h`}
            width={40}
            domain={[0, yMax]}
          />
          <Tooltip content={<CustomTooltip />} />

          {/* Pre-HG average ± std gray band (no label — shown in card header) */}
          {data.pre_hg_avg > 0 && (
            <ReferenceArea
              y1={Math.max(0, data.pre_hg_avg - preHgStd)}
              y2={data.pre_hg_avg + preHgStd}
              fill={COLORS.preHgBand}
              fillOpacity={0.18}
              strokeOpacity={0}
              ifOverflow="extendDomain"
            />
          )}

          {/* Weekly hours line — stroke colored by utilization gradient */}
          <Line
            type="monotone"
            dataKey="hours"
            name="Weekly Hours"
            stroke={`url(#${gradientId})`}
            strokeWidth={2.5}
            dot={<UtilizationDot />}
            activeDot={<UtilizationDot r={6} />}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
